import type { $Fetch } from 'nitropack'
import type { FetchOptions } from 'ofetch'
import { useAlertStore } from '@/store/alert'

import { DEFAULT_LANG } from '@/i18n.config'
import type { ApiResponseType } from '@/types/service'

const isDev = process.env.NODE_ENV === 'development'

export const createApiClient = (options?: FetchOptions, nuxtApp = useNuxtApp()) => {
  const config = useRuntimeConfig()
  const paymentApiList = [
    '/v2/niceday/payment/none',
    '/v2/niceday/payment/pay-by-line',
    '/v2/niceday/payment/pay-by-credit-installment',
    '/v2/niceday/payment/pay-by-credit',
  ]
  const skipGeneralAlertList = [...paymentApiList, '/v2/niceday/auth', '/v2/niceday/promo', '/v2/niceday/register', 'v2/niceday/login']
  const baseURL = isDev ? '/api/proxy' : config.public.apiBaseUrl

  // Capture context here, not inside callbacks — avoids "[nuxt] instance unavailable" during SSR $fetch retry.
  const { localeProperties } = nuxtApp.$i18n
  const alertStore = useAlertStore(nuxtApp.$pinia)

  return $fetch.create({
    baseURL: baseURL,
    credentials: 'include',
    onRequest({ options }) {
      const acceptLang = localeProperties.value.name || DEFAULT_LANG
      options.headers = new Headers(options.headers)
      options.headers.set('Accept-Language', acceptLang)
      if (import.meta.client) {
        options.headers.set('Accept-Timezone', getTimeZone())
      } else {
        options.headers.set('Server-Side-Request', 'true')
      }
    },
    onResponseError(context) {
      // FIXME: 過濾掉檢查登入時的錯誤訊息，但應該有更乾淨的做法
      if (context.response._data?.message && !skipGeneralAlertList.some(path => context.response.url.includes(path))) {
        alertStore.error(context.response._data.message)
      }
    },
    ...options,
  })
}

const useApiClient = <T>(): $Fetch<ApiResponseType<T>> => {
  const nuxtApp = useNuxtApp()

  if (!nuxtApp._apiClient) {
    nuxtApp._apiClient = createApiClient(undefined, nuxtApp)
  }

  return nuxtApp._apiClient as $Fetch<ApiResponseType<T>>
}

export default useApiClient
